import { useEffect, useState } from "react";
import { Loader2, CheckCircle, XCircle, Clock } from "lucide-react";
import { fmt } from "@/utils/format";

const STATUS_META = {
  pending: { label: "Hazırlanıyor...", barColor: "bg-slate-400", textColor: "text-slate-600" },
  running: { label: "Aktarılıyor...",  barColor: "bg-[#0D1B3E]",  textColor: "text-[#0D1B3E]" },
  done:    { label: "Tamamlandı",      barColor: "bg-green-500",   textColor: "text-green-600" },
  error:   { label: "Hata oluştu",     barColor: "bg-red-500",     textColor: "text-red-600"   },
};

/**
 * Displays live progress for a background Excel import job.
 * Props: jobStatus ({status, progress, total, processed, inserted, updated, message})
 */
export function ImportProgress({ jobStatus }) {
  const [elapsed, setElapsed] = useState(0);

  // Elapsed-time ticker (resets when job finishes)
  useEffect(() => {
    if (jobStatus.status !== "pending" && jobStatus.status !== "running") return;
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [jobStatus.status]);

  const meta     = STATUS_META[jobStatus.status] ?? STATUS_META.pending;
  const progress = jobStatus.progress ?? 0;
  const isActive = jobStatus.status === "pending" || jobStatus.status === "running";
  const total    = jobStatus.total ?? 0;
  const processed = jobStatus.processed ?? 0;
  const rate     = elapsed > 0 ? Math.round(processed / elapsed) : 0;
  const etaSec   = rate > 0 && total > processed ? Math.round((total - processed) / rate) : null;

  const fmtTime = (s) => {
    if (s === null) return "—";
    if (s < 60) return `${s}s`;
    return `${Math.floor(s / 60)}d ${s % 60}s`;
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-5">
      {/* Status header */}
      <div className="flex items-center gap-3">
        {isActive ? (
          <Loader2 size={24} className="text-[#0D1B3E] animate-spin shrink-0" />
        ) : jobStatus.status === "done" ? (
          <CheckCircle size={24} className="text-green-500 shrink-0" />
        ) : (
          <XCircle size={24} className="text-red-500 shrink-0" />
        )}
        <div className="min-w-0">
          <h2 className="font-semibold text-slate-800">{meta.label}</h2>
          <p className="text-sm text-slate-500 truncate">{jobStatus.message ?? "..."}</p>
        </div>
        {isActive && (
          <div className="ml-auto flex items-center gap-1 text-xs text-slate-400 shrink-0">
            <Clock size={12} />
            <span className="tabular-nums">{fmtTime(elapsed)}</span>
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex justify-between items-center mb-1.5">
          <span className="text-xs text-slate-500 font-medium uppercase tracking-wider">İlerleme</span>
          <span className={`text-sm font-bold tabular-nums ${meta.textColor}`}>{progress}%</span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
          <div
            className={`h-3 rounded-full ${meta.barColor} transition-all duration-700 ease-out`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Stats grid */}
      {total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Toplam Satır",    value: fmt(total, 0) },
            { label: "İşlenen",         value: fmt(processed, 0) },
            { label: "Eklenen",         value: fmt(jobStatus.inserted ?? 0, 0),  accent: "text-green-600" },
            { label: "Güncellenen",     value: fmt(jobStatus.updated  ?? 0, 0),  accent: "text-blue-600"  },
          ].map(({ label, value, accent }) => (
            <div key={label} className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
              <div className={`text-lg font-bold tabular-nums ${accent ?? "text-slate-800"}`}>{value}</div>
              <div className="text-xs text-slate-500 mt-0.5 leading-snug">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Speed / ETA row */}
      {isActive && total > 0 && (
        <div className="flex justify-between text-xs text-slate-400 tabular-nums">
          <span>Hız: ~{fmt(rate, 0)} satır/s</span>
          <span>Tahmini bitiş: {fmtTime(etaSec)}</span>
        </div>
      )}

      {isActive && (
        <p className="text-xs text-slate-400 text-center">
          Sayfa kapatılsa bile içe aktarma arka planda devam eder.
        </p>
      )}
    </div>
  );
}
